
import { Component, Ref, Vue, Watch } from 'vue-property-decorator'
import { Observer } from 'mobx-vue'
import PopLayout from '../layout/index.vue'
import DetailSkeleton from './Skeleton.vue'
import DetailMain from './Main.vue'
import UserContentEditor from '../editor/index.vue'
import UserContent from '../content/index.vue'
import { EnumApiCourse, EnumApiHomework } from '@xt/client/api'
import { $service } from '@xt/client/experimental/request/service'
import type { ClasshourDetail as TClasshourDetail } from '@xt/client/entities/course-v2/types.d'
import type { DairyOption, HomeworkOption } from '@xt/client/entities/course-v2/user-content'
import { EnumHomeworkBizType } from '@xt/client'
import { Bind, Debounce, Throttle } from 'lodash-decorators'

type ToggleSlideParams =
  | {
      type: 'homework-content'
      // 用户点击的作业id
      id: number
    }
  | {
      type: 'homework-submit'
      // 作业任务id
      taskId: number
    }

@Observer
@Component({
  components: {
    PopLayout,
    DetailSkeleton,
    DetailMain,
    UserContentEditor,
    UserContent
  }
})
export default class ClasshourDetail extends Vue {
  @Ref()
  editor?: {
    getFormStatus(): { hasImage: boolean; hasContent: boolean; hasTitle: boolean }
  }

  @Ref()
  popLayout: {
    innerVisible: boolean
    open: () => void
    hide: () => void
  }

  // 当前课时详情弹窗的课时id
  classhourId: null | number = null
  // 数据正在获取
  dataFetching = true
  // 课时详情数据
  classhourDetail: TClasshourDetail | null = null
  // 当前的slide index
  current: 0 | 1 = 0
  // slide 1 的类型 要不是作业详情 要不是提交作业
  slideType: 'homework-content' | 'homework-submit' | null = null
  // 副slide样式
  subSlideStyle: string = ''
  // 监听主视图高度变化
  rob: ResizeObserver | null = null
  // 用户内容详情弹窗 此处挂载的一定是作业
  userContentOptions: HomeworkOption | DairyOption | null = null
  // 组件强制渲染
  userContentReloadKey = 0
  // 作业编辑配置
  userContentEditorOptions = {
    type: 'homework',
    showBackButton: true,
    contentRequired: true,
    imageRequired: true,
    maxImageSize: 9,
    editorTitle: '作业内容',
    uploaderTitle: '作业图片',
    descriptionTitle: '作业详情',
    description: '',
    isLoading: true,
    contentPlaceholder: '',
    // 占位 只在提交作业的时候用到
    taskId: 0
  }

  showMockPop(data) {
    this.popLayout.open()
    this.classhourDetail = data
    this.dataFetching = false
  }

  // 暴露给父组件
  showPop(classhourId: number) {
    this.classhourId = classhourId
    this.popLayout.open()
    this.fetchClasshourDetail(this.classhourId)
  }

  // 暴露给父组件
  hidePop() {
    this.popLayout.hide()
  }

  @Debounce(500, { leading: false, trailing: true })
  @Bind()
  subChangeCallback(entities: ResizeObserverEntry[]) {
    if (entities[0]) {
      console.warn('ResizeObserver回调执行！')
      this.subSlideStyle = `height: ${entities[0].contentRect.height}px;`
    }
  }

  // transition组件自带的动画效果
  setSubSlideStyle() {
    this.$nextTick(() => {
      const el = document.getElementById('main-slide-ob-target')
      if (el) {
        this.subSlideStyle = `height: ${el.offsetHeight}px;`
        if (this.rob) {
          console.log('销毁旧的ResizeObserver')
          this.rob.disconnect()
          this.rob = null
        }

        if (window.ResizeObserver) {
          console.log('创建ResizeObserver')
          this.rob = new ResizeObserver(this.subChangeCallback)
          this.rob.observe(el)
        }
      }
    })
  }

  get GuideData() {
    return this.$store.$courseV2.GuideData
  }

  get PageStore() {
    return this.$store.$courseV2
  }

  async fetchClasshourDetail(classhourId: number) {
    const [error, data] = await $service<TClasshourDetail>({
      url: EnumApiCourse.CourseMapDetails,
      method: 'GET',
      data: { id: classhourId },
      params: { id: classhourId, requiredTaskPublish: 'true' }
    })

    if (error) return

    // FIXME
    setTimeout(() => {
      if (this.popLayout.innerVisible) {
        this.classhourDetail = data
        this.dataFetching = false
      }
    }, 350)
  }

  // 当弹窗被完全关闭后 将所有额外状态恢复至初始状态
  handleClosed() {
    this.classhourId = null
    this.dataFetching = true
    this.classhourDetail = null
    this.current = 0
    this.slideType = null
    this.userContentOptions = null
    this.subSlideStyle = ''
    if (this.rob) {
      console.log('弹窗关闭 销毁旧的ResizeObserver')
      this.rob.disconnect()
      this.rob = null
    }
    // 点击课时详情之后再点击日记出现获取不到userContentRef的情况
    this.PageStore.UserContent.reset()
  }

  // 假数据切换到二级内容区域
  handleMockToggleNext(params) {
    if (params.type === 'homework-submit') {
      this.userContentEditorOptions = params.options
      this.slideType = params.type
      this.current = 1
    } else if (params.type === 'homework-content') {
      this.userContentOptions = {
        type: 'homework',
        homeworkId: 1,
        momentId: '1'
      } as HomeworkOption | DairyOption
      this.$store.$courseV2.UserContent.loadMockContent(params)
      this.slideType = params.type
      this.current = 1
    }
  }
  // 切换到二级内容区域
  async handleToggleNext(params: ToggleSlideParams) {
    if (params.type === 'homework-content') {
      this.userContentOptions = {
        type: 'homework',
        homeworkId: params.id
      }

      this.slideType = params.type
      this.current = 1
    } else if (params.type === 'homework-submit') {
      // 先清除之前的旧数据
      this.userContentEditorOptions.description = ''

      this.slideType = params.type
      this.current = 1

      this.userContentEditorOptions.isLoading = true

      const data = await this.PageStore.getHomeworkTaskDetail(params.taskId)

      this.userContentEditorOptions.isLoading = false

      if (!data) {
        // 接口错误 返回到上一级
        this.slideType = 'homework-content'
        this.current = 0
        return
      }

      this.userContentEditorOptions.contentPlaceholder =
        data.homeworkBizType === EnumHomeworkBizType.ThinkAndRead
          ? '于读书与思索之中，生出智慧和明澈的洞见。'
          : '在完成作业的过程中，磨手练心，积累功夫，这便是实实在在的进步。'
      this.userContentEditorOptions.taskId = params.taskId
      this.userContentEditorOptions.description = data.description
      this.userContentEditorOptions.imageRequired = data.imageRequired
      this.userContentEditorOptions.contentRequired = data.contentRequired
    }
  }

  // 切换回主内容区
  handleTogglePre() {
    this.checkPreventClose({
      onNext: () => {
        this.current = 0

        setTimeout(() => {
          if (this.current === 0) {
            console.log('已经切换回了主内容区域， 销毁二级区域')
            this.PageStore.UserContent.reset()
            this.userContentReloadKey += 1
          }
        }, 350)
      }
    })
  }

  // 用户成功删除了数据
  handleRemoveContentSuccess(payload: { type: 'homework'; homeworkId: number }) {
    this.current = 0

    if (this.classhourDetail && payload.type === 'homework') {
      this.classhourDetail.homeworkTaskList = this.classhourDetail.homeworkTaskList.map(item => {
        if (Array.isArray(item.memberHomeworks)) {
          item.memberHomeworks = item.memberHomeworks.filter(i => i.id !== payload.homeworkId)
        }

        return item
      })
    }
  }

  // 如果用户输入了一些内容 需要阻拦一下
  checkPreventClose({ onNext }: { onNext: () => void }) {
    if (this.slideType === 'homework-submit' && this.current === 1 && this.editor) {
      const { hasContent, hasImage, hasTitle } = this.editor.getFormStatus()

      if (hasContent || hasImage || hasTitle) {
        this.$confirmV2({
          desc: '你的作业还未提交',
          okText: '坚持离开',
          cancelText: '取消',
          clickToClose: false,
          onOk: onNext,
          onException: onNext
        })

        return
      }
    }

    onNext()
  }

  // 刷新主面板数据，如：作业提交成功，提交批阅成功
  reloadDetail() {
    if (this.classhourId) this.fetchClasshourDetail(this.classhourId)

    this.current = 0
    this.userContentReloadKey += 1
  }

  handelClickOutside() {
    this.checkPreventClose({
      onNext: this.hidePop
    })
  }

  created() {}
  mounted() {}
  updated() {}
  destroyed() {}
}
